import LoadingButton from "@/components/common/form/loading-button";
import V2Avatar from "@/components/v2/common/v2-avatar";
import { useOrgAndSiteContext } from "@/context";
import { removeStoredSelectedSite, storeSelectedTeam } from "@/utils/common";
import useClickOutside from "@shared/common/hooks/use-click-outside";
import { IOrganization } from "@shared/common/types/api-private/generated/graphql";
import { Tooltip } from "antd";
import {
  Badge,
  ChevronDown,
  ChevronUp,
  CreditCard,
  LogOut,
  Settings,
  UsersRound,
} from "lucide-react";
import { signOut, useSession } from "next-auth/react";
import Link from "next/link";
import React, { useCallback, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { CSSTransition } from "react-transition-group";
import { twMerge } from "tailwind-merge";
import useFacebookPixel from "../tracking/facebook/pixel";
import useKakaoPixel from "../tracking/kakao/pixel";
import useNaverGfa from "../tracking/naver/gfa";

interface IProfileBtnProps {}

const v2UrlPrefix = process.env.NEXT_PUBLIC_V2_URL_PREFIX;

const ProfileBtn: React.FC<IProfileBtnProps> = () => {
  const { t } = useTranslation();
  const [showPopupMenu, setShowPopupMenu] = useState(false);
  const { data: session, status } = useSession();

  const { sendKakaoPixelParticipation } = useKakaoPixel();
  const { sendFbq } = useFacebookPixel();
  const { sendNaverGfaConversion } = useNaverGfa();

  const closePopupMenu = useCallback(() => {
    setShowPopupMenu(false);
  }, [setShowPopupMenu]);

  const { organizations, currentOrganization } = useOrgAndSiteContext();

  const { setDom: setClickOutsideDom } = useClickOutside(closePopupMenu);

  const [collapsedTeam, setCollapsedTeam] = useState(true);

  const nodeRef = useRef(null);

  const renderOrganizationInfo = useCallback(
    (organization: IOrganization) => {
      return (
        <>
          <div className="flex items-center min-w-0">
            <div className="truncate">{organization.name}</div>
            {["STARTER", "BASIC", "PRO", "BUSINESS"].includes(
              organization.plan || ""
            ) ? (
              <Tooltip
                title={`${organization.plan} PLAN`}
                placement="top"
                trigger={["hover", "focus"]}
              >
                <div className="relative flex-none ml-0.5">
                  <Badge
                    className="w-5 h-5"
                    strokeWidth={0}
                    fill={
                      organization.plan === "STARTER"
                        ? "#f1c40f"
                        : organization.plan === "BASIC"
                        ? "#27ae60"
                        : organization.plan === "PRO"
                        ? "#e74c3c"
                        : organization.plan === "BUSINESS"
                        ? "#8e44ad"
                        : ""
                    }
                  />
                  <span className="absolute text-[0.625rem] font-bold text-white -translate-x-1/2 -translate-y-1/2 top-1/2 left-1/2">
                    {organization.plan?.[0]}
                  </span>
                </div>
              </Tooltip>
            ) : (
              <></>
            )}
          </div>
          <span className="text-xs text-cb-gray-700 shrink-0">
            {organization.userRole === "OWNER" ? t("role.owner") : ""}
          </span>
        </>
      );
    },
    [t]
  );

  const loading = status === "loading";

  if (loading) return <></>;

  return session ? (
    <>
      <button
        className={twMerge(
          "flex center-center cursor-pointer w-[2.25rem] h-[2.25rem] rounded-full",
          showPopupMenu && "border border-cb-brand"
        )}
        onClick={() => setShowPopupMenu(!showPopupMenu)}
        data-gtm-id="profile_dropdown"
      >
        <V2Avatar value={session.user?.image} width={32} height={32} />
      </button>
      <CSSTransition
        nodeRef={nodeRef}
        in={showPopupMenu}
        classNames="popup-transition"
        timeout={200}
        unmountOnExit
      >
        <div
          className={twMerge("right-0 w-64 top-14 popup-menu")}
          ref={nodeRef}
        >
          <div ref={setClickOutsideDom}>
            <div className="flex flex-col gap-2 py-2 text-center rounded-t bg-cb-gray-50">
              <div className="w-[40px] mx-auto">
                <V2Avatar value={session.user?.image} width={40} height={40} />
              </div>
              <div className="flex flex-col px-2">
                <div className="text-xs text-cb-gray-700">
                  {t("gnb:profilePopup.hello")}
                </div>
                <Link
                  href={`${v2UrlPrefix}/me`}
                  className="flex justify-center hover:underline hover:text-cb-gray-900"
                  data-gtm-id="profile_me"
                >
                  <span className="truncate">{session.user?.name}</span>
                  <span>{t("gnb:profilePopup.sir")}</span>
                </Link>
              </div>
            </div>

            <div className="mt-2">
              <button
                className={twMerge(
                  "flex items-center justify-between w-full px-2 h-9 hover:bg-gray-100",
                  !collapsedTeam && "bg-gray-200 hover:bg-gray-200"
                )}
                onClick={() => setCollapsedTeam(!collapsedTeam)}
                data-gtm-id="profile_team_dropdown"
              >
                {currentOrganization ? (
                  <div className="flex items-center min-w-0 gap-2">
                    {renderOrganizationInfo(currentOrganization)}
                  </div>
                ) : (
                  <></>
                )}
                {collapsedTeam ? (
                  <ChevronDown className="w-4 h-4 shrink-0" />
                ) : (
                  <ChevronUp className="w-4 h-4 shrink-0" />
                )}
              </button>
            </div>

            <ul
              className={twMerge(
                "flex flex-col gap-1 overflow-y-auto border-cb-gray-100 max-h-56 mt-1",
                collapsedTeam && "hidden"
              )}
            >
              {organizations &&
                organizations.length > 0 &&
                organizations.map((organization) => (
                  <li key={organization.id}>
                    <Link
                      href={`${v2UrlPrefix}/${organization.slug}`}
                      legacyBehavior
                    >
                      <a
                        className={twMerge(
                          "item px-2 h-9 hover:bg-cb-gray-100 hover:text-cb-gray-800",
                          currentOrganization?.id === organization.id &&
                            "bg-cb-gray-200 text-cb-gray-900 hover:bg-cb-gray-200 hover:text-cb-gray-900"
                        )}
                        onClick={() => {
                          setShowPopupMenu(false);
                          storeSelectedTeam(organization);
                          if (organization.id !== currentOrganization?.id) {
                            removeStoredSelectedSite();
                          }
                        }}
                        data-gtm-id="profile_switch_team"
                      >
                        <span className="flex items-center w-full gap-2">
                          {renderOrganizationInfo(organization)}
                        </span>
                      </a>
                    </Link>
                  </li>
                ))}
            </ul>

            <ul className="flex flex-col gap-1 py-2 mt-2 border-t border-cb-gray-100">
              <li>
                {organizations && organizations.length > 0 && (
                  <Link
                    href={`${v2UrlPrefix}/${
                      currentOrganization
                        ? currentOrganization.slug
                        : organizations[0]?.slug
                    }/team`}
                    className="flex items-center gap-2 px-2 h-9 hover:text-cb-gray-900 hover:bg-cb-gray-100"
                    onClick={() => setShowPopupMenu(false)}
                    data-gtm-id="profile_team_management"
                  >
                    <UsersRound className="w-4 h-4" />
                    <span>{t("gnb:profilePopup.team")}</span>
                  </Link>
                )}
              </li>
              {currentOrganization?.userRole === "OWNER" && (
                <li>
                  {organizations && organizations.length > 0 && (
                    <Link
                      href={`${v2UrlPrefix}/${
                        currentOrganization
                          ? currentOrganization.slug
                          : organizations[0]?.slug
                      }/payment`}
                      className="flex items-center gap-2 px-2 h-9 hover:text-cb-gray-900 hover:bg-cb-gray-100"
                      onClick={() => {
                        setShowPopupMenu(false);
                        // TODO: 추후 트래킹 적극적으로 사용하게되면 CustomLink 컴포넌트로 변경
                        sendFbq("trackCustom", "menu_payment");
                        sendKakaoPixelParticipation("menu_payment");
                        sendNaverGfaConversion("3", "0");
                      }}
                      data-gtm-id="profile_payment"
                    >
                      <CreditCard className="w-4 h-4" />
                      <span>{t("gnb:profilePopup.plan")}</span>
                    </Link>
                  )}
                </li>
              )}
              <li>
                {organizations && organizations.length > 0 && (
                  <Link
                    href={`${v2UrlPrefix}/${
                      currentOrganization
                        ? currentOrganization.slug
                        : organizations[0]?.slug
                    }/site`}
                    className="flex items-center gap-2 px-2 h-9 hover:text-cb-gray-900 hover:bg-cb-gray-100"
                    onClick={() => setShowPopupMenu(false)}
                    data-gtm-id="profile_site"
                  >
                    <Settings className="w-4 h-4" />
                    <span>{t("gnb:profilePopup.site")}</span>
                  </Link>
                )}
              </li>
            </ul>

            <ul className="flex flex-col gap-0.5 py-2 border-t border-cb-gray-100">
              <li>
                <a
                  className="flex items-center gap-2 px-2 h-9 hover:text-cb-gray-900 hover:bg-cb-gray-100"
                  onClick={() =>
                    signOut({
                      callbackUrl: `${v2UrlPrefix}/login`,
                    })
                  }
                  tabIndex={0}
                  data-gtm-id="profile_logout"
                >
                  <LogOut className="w-4 h-4" />
                  <span>{t("gnb:profilePopup.logout")}</span>
                </a>
              </li>
            </ul>
          </div>
        </div>
      </CSSTransition>
    </>
  ) : (
    <LoadingButton
      theme="primary"
      type="link"
      href={`${v2UrlPrefix}/login`}
      className="py-[7px] px-[10px] lg:px-8"
    >
      {t("gnb:profilePopup.login")}
    </LoadingButton>
  );
};

export default ProfileBtn;
